import React, { useState, useEffect } from 'react';
import Modal, { ModalCloseButton } from './components/Modal';
import ConfirmDialog from './components/ConfirmDialog';
import { apiFetch } from './api';

const API = '/api/auth';

// Kleines gestapeltes Modal: neues Passwort für einen Benutzer setzen (nur Admin)
function PasswordModal({ user, headers, onClose, onSuccess }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const res = await apiFetch(`${API}/users/${user.id}/password`, {
        method: 'POST', headers,
        body: JSON.stringify({ password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || `Fehler ${res.status}`); return; }
      onSuccess();
    } catch (err) {
      console.error('Fehler beim Passwort-Ändern:', err);
      setError('Ändern fehlgeschlagen — bitte Verbindung prüfen und erneut versuchen.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} label={`Passwort ändern — ${user.username}`} style={{ maxWidth: 420 }}>
      <h2 style={{ marginBottom: 10 }}>Passwort ändern</h2>
      <p style={{ fontSize: 14, color: '#374151', marginBottom: 14 }}>
        Neues Passwort für <strong>{user.username}</strong> festlegen.
      </p>
      <form onSubmit={handleSubmit}>
        <div className="form-field">
          <label htmlFor="reset-password">Neues Passwort</label>
          <input
            id="reset-password"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            minLength={6}
            autoComplete="new-password"
            autoFocus
            required
            aria-describedby="reset-password-hint"
          />
          <span id="reset-password-hint" style={{ fontSize: 11, color: '#6b7280' }}>Mindestens 6 Zeichen</span>
        </div>
        {error && <div className="login-error" role="alert" style={{ marginTop: 8 }}>{error}</div>}
        <div className="form-actions" style={{ marginTop: 16 }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
          <button
            type="submit"
            className={`btn btn-primary${saving ? ' btn-loading' : ''}`}
            disabled={saving}
            aria-busy={saving}
          >
            {saving ? 'Speichern...' : 'Passwort speichern'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default function UserAdmin({ token, onClose }) {
  const [users, setUsers] = useState([]);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [creating, setCreating] = useState(false);
  const [confirmUser, setConfirmUser] = useState(null);
  const [pwUser, setPwUser] = useState(null);
  const [loadError, setLoadError] = useState('');

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoadError('');
    try {
      const res = await apiFetch(`${API}/users`, { headers });
      if (!res.ok) throw new Error(`Fehler ${res.status}`);
      setUsers(await res.json());
    } catch (err) {
      console.error('Fehler beim Laden der Benutzer:', err);
      setLoadError('Benutzer konnten nicht geladen werden.');
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async e => {
    e.preventDefault();
    setError(''); setSuccess('');
    setCreating(true);
    try {
      const res = await apiFetch(`${API}/register`, {
        method: 'POST', headers,
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || `Fehler ${res.status}`); return; }
      setSuccess(`Benutzer "${username}" erstellt`);
      setUsername(''); setPassword('');
      load();
    } catch (err) {
      console.error('Fehler beim Erstellen:', err);
      setError('Erstellen fehlgeschlagen — bitte Verbindung prüfen und erneut versuchen.');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (user) => {
    setConfirmUser(null);
    try {
      const res = await apiFetch(`${API}/users/${user.id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error(`Fehler ${res.status}`);
      load();
    } catch (err) {
      console.error('Fehler beim Löschen:', err);
      setError('Löschen fehlgeschlagen — bitte erneut versuchen.');
    }
  };

  return (
    <Modal onClose={onClose} label="Benutzerverwaltung">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2>Benutzerverwaltung</h2>
          <ModalCloseButton onClose={onClose} />
        </div>

        {/* Neuen Benutzer anlegen */}
        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 20 }}>
          <div className="section-label" style={{ marginBottom: 12 }}>Neuen Benutzer anlegen</div>
          <form onSubmit={handleCreate}>
            <div className="form-grid">
              <div className="form-field">
                <label htmlFor="new-username">Benutzername</label>
                <input
                  id="new-username"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  placeholder="z.B. max.mustermann"
                  autoComplete="off"
                  autoCapitalize="none"
                  required
                />
              </div>
              <div className="form-field">
                <label htmlFor="new-password">Passwort</label>
                <input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  minLength={6}
                  autoComplete="new-password"
                  required
                  aria-describedby="new-password-hint"
                />
                <span id="new-password-hint" style={{ fontSize: 11, color: '#6b7280' }}>Mindestens 6 Zeichen</span>
              </div>
            </div>
            {error && <div className="login-error" role="alert" style={{ marginTop: 8 }}>{error}</div>}
            {success && <div className="import-result" role="status" style={{ marginTop: 8 }}>{success}</div>}
            <button
              type="submit"
              className={`btn btn-gradient${creating ? ' btn-loading' : ''}`}
              style={{ marginTop: 12 }}
              disabled={creating}
              aria-busy={creating}
            >
              {creating ? 'Erstellen...' : 'Benutzer erstellen'}
            </button>
          </form>
        </div>

        {/* Benutzerliste */}
        <div className="section-label" style={{ marginBottom: 8 }}>Alle Benutzer ({users.length})</div>
        {loadError && (
          <div className="login-error" role="alert" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ flex: 1 }}>{loadError}</span>
            <button type="button" className="btn btn-secondary" style={{ fontSize: 12, padding: '6px 12px', minHeight: 36 }} onClick={load}>Erneut laden</button>
          </div>
        )}
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          {users.map((u, i) => (
            <div key={u.id} style={{
              display: 'flex', alignItems: 'center', padding: '10px 14px',
              borderTop: i > 0 ? '1px solid #f3f4f6' : 'none',
              background: 'white',
            }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: u.role === 'admin' ? 'linear-gradient(135deg,#3b3be8,#c084fc)' : '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: u.role === 'admin' ? 'white' : '#6b7280', fontWeight: 700, fontSize: 13, marginRight: 12 }}>
                {u.username[0].toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{u.username}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>{u.role === 'admin' ? 'Administrator' : 'Vertriebler'} · seit {new Date(u.created_at).toLocaleDateString('de-DE')}</div>
              </div>
              {u.role !== 'admin' && (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-secondary" style={{ fontSize: 12, padding: '6px 12px', minHeight: 36 }} onClick={() => { setSuccess(''); setPwUser(u); }}>Passwort</button>
                  <button className="btn btn-danger" style={{ fontSize: 12, padding: '6px 12px', minHeight: 36 }} onClick={() => setConfirmUser(u)}>Löschen</button>
                </div>
              )}
            </div>
          ))}
        </div>
        {confirmUser && (
          <ConfirmDialog
            title="Benutzer löschen?"
            message={`"${confirmUser.username}" wird unwiderruflich gelöscht.\nZugewiesene Leads und Gebiete werden freigegeben, eigene Termine gelöscht.`}
            onConfirm={() => handleDelete(confirmUser)}
            onCancel={() => setConfirmUser(null)}
          />
        )}
        {pwUser && (
          <PasswordModal
            user={pwUser}
            headers={headers}
            onClose={() => setPwUser(null)}
            onSuccess={() => {
              setPwUser(null);
              setError('');
              setSuccess(`Passwort für "${pwUser.username}" geändert`);
            }}
          />
        )}
    </Modal>
  );
}
